import React, { useState, useEffect } from 'react';
import { Search, X, ChevronRight, FlaskConical, AlertTriangle } from 'lucide-react';
import { api } from '../../api';

export const ScheduleWorkshopModal = ({ onClose, onSuccess, preselectedCourse }) => {
  const [courses, setCourses] = useState([]);
  const [courseSearch, setCourseSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAllCourses, setShowAllCourses] = useState(false);
  const [requestLabs, setRequestLabs] = useState(false);
  const [formData, setFormData] = useState({
    course_id: preselectedCourse?.id || '', delivery_date: '', location: '', expected_attendees: '', notes: '',
    expected_lab_attendees: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const allCourses = await api.request('/courses');
        setCourses(Array.isArray(allCourses) ? allCourses : allCourses.courses || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchCourses();
  }, []);

  useEffect(() => {
    setRequestLabs(false);
    setFormData(prev => ({ ...prev, expected_lab_attendees: '' }));
  }, [formData.course_id]);

  const filteredCourses = courses.filter(c =>
    c.title && c.title.toLowerCase().includes((courseSearch || '').toLowerCase())
  );

  const displayedCourses = showAllCourses ? filteredCourses : filteredCourses.slice(0, 10);
  const hasMoreCourses = filteredCourses.length > 10;
  const selectedCourse = preselectedCourse || courses.find(c => c.id === formData.course_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await api.request('/workshops', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          expected_attendees: parseInt(formData.expected_attendees),
          delivery_date: formData.delivery_date
        })
      });
      if (selectedCourse?.hands_on_lab && requestLabs && formData.expected_lab_attendees) {
        try {
          await api.request('/lab-requests', {
            method: 'POST',
            body: JSON.stringify({
              workshop_id: result.id || null,
              course_id: selectedCourse.id,
              course_title: selectedCourse.title,
              workshop_date: formData.delivery_date,
              location: formData.location,
              expected_attendees: parseInt(formData.expected_attendees),
              expected_lab_attendees: parseInt(formData.expected_lab_attendees),
            })
          });
        } catch (labErr) {
          console.error('Lab request submission failed:', labErr);
        }
      }
      onSuccess();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full my-8 flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-xl font-bold text-levelup-dark">Schedule Workshop</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={24} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4">
        <form id="schedule-workshop-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Course</label>
              {preselectedCourse ? (
                <div className="w-full px-4 py-2.5 bg-gray-100 border rounded-lg text-sm text-gray-700">
                  {preselectedCourse.title}
                </div>
              ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                  <input
                    type="text"
                    className="w-full pl-10 pr-10 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-levelup-accent focus:border-transparent"
                    placeholder="Search and select a course..."
                    value={showDropdown ? courseSearch : (selectedCourse?.title || '')}
                    onChange={(e) => setCourseSearch(e.target.value)}
                    onFocus={() => { setShowDropdown(true); setCourseSearch(''); }}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                    data-testid="course-search-input"
                  />
                  <ChevronRight className="absolute right-3 top-1/2 transform -translate-y-1/2 rotate-90 text-gray-400 pointer-events-none" size={16} />
                </div>
                {showDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {displayedCourses.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500">No courses found</div>
                    ) : (
                      <>
                        {displayedCourses.map(c => (
                          <button
                            key={c.id}
                            type="button"
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-levelup-light transition-colors ${formData.course_id === c.id ? 'bg-levelup-light font-medium' : ''}`}
                            onMouseDown={() => {
                              setFormData({...formData, course_id: c.id});
                              setShowDropdown(false);
                            }}
                          >
                            {c.title}
                          </button>
                        ))}
                        {hasMoreCourses && !showAllCourses && (
                          <button
                            type="button"
                            onMouseDown={() => setShowAllCourses(true)}
                            className="w-full text-left px-4 py-2 text-sm text-levelup-teal hover:bg-gray-50"
                          >
                            Show more... ({filteredCourses.length - 10} more)
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Workshop Date</label>
              <input
                type="date"
                className="input-field"
                value={formData.delivery_date}
                onChange={(e) => setFormData({...formData, delivery_date: e.target.value})}
                min={new Date().toISOString().split('T')[0]}
                required
                data-testid="schedule-date-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                className="input-field"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="City, Country or Virtual"
                required
                data-testid="schedule-location-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expected Attendees</label>
              <input
                type="number"
                className="input-field"
                value={formData.expected_attendees}
                onChange={(e) => setFormData({...formData, expected_attendees: e.target.value})}
                min="1"
                required
                data-testid="schedule-attendees-input"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
              <textarea
                className="input-field"
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="E.g., Campaign: FY27 Q3 Cloud Skills Push&#10;Target Audience: Azure-focused ISVs&#10;Motion: Reseller enablement for mid-market&#10;Special requirements: Spanish language delivery"
                data-testid="schedule-notes-input"
              />
            </div>
          </div>

          {selectedCourse?.hands_on_lab && (
            <div className="border-2 border-blue-200 rounded-xl overflow-hidden">
              <div className="bg-blue-50 px-5 py-4 space-y-3">
                <div className="flex items-start gap-3">
                  <FlaskConical size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-blue-900 font-semibold text-sm">This course includes Interactive Simulated Labs</p>
                    <p className="text-blue-700 text-xs mt-0.5 leading-relaxed">
                      This workshop course contains hands-on lab activities for participants. You can request interactive simulated lab provisioning below.
                    </p>
                  </div>
                </div>
                <label className="flex items-center gap-3 cursor-pointer bg-white border border-blue-200 rounded-lg px-4 py-3 hover:bg-blue-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={requestLabs}
                    onChange={e => setRequestLabs(e.target.checked)}
                    className="w-4 h-4 rounded border-blue-400 text-blue-600 focus:ring-blue-400"
                    data-testid="request-labs-checkbox"
                  />
                  <span className="text-sm font-semibold text-blue-900">Place a request for simulated labs for this workshop</span>
                </label>
              </div>

              {requestLabs && (
                <div className="bg-blue-50 px-5 pb-4 space-y-4 border-t border-blue-100">
                  <div className="bg-blue-600 -mx-5 px-5 py-2.5 flex items-center gap-2">
                    <FlaskConical size={16} className="text-white flex-shrink-0" />
                    <h3 className="text-white font-bold text-sm">Request Interactive Simulated Labs</h3>
                  </div>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    Fill this form to place your request for the provisioning of interactive simulated lab experience for your workshop participants.
                  </p>
                  <div>
                    <label className="block text-sm font-semibold text-blue-900 mb-1">
                      Expected no. of lab attendees <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      className="w-48 px-4 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white"
                      value={formData.expected_lab_attendees}
                      onChange={(e) => setFormData({...formData, expected_lab_attendees: e.target.value})}
                      placeholder="e.g. 25"
                      required={requestLabs}
                      data-testid="lab-attendees-input"
                    />
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-1.5">
                    {formData.delivery_date && (() => {
                      const daysUntil = Math.ceil((new Date(formData.delivery_date) - new Date()) / (1000 * 60 * 60 * 24));
                      return daysUntil < 14 ? (
                        <div className="flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg px-3 py-2.5 mb-3">
                          <AlertTriangle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                          <p className="text-red-700 text-xs font-semibold leading-relaxed">
                            Your workshop is in {daysUntil <= 0 ? 'less than 1' : daysUntil} day{daysUntil !== 1 ? 's' : ''} — lab provisioning requires a minimum of 2 weeks. Submit this request immediately to avoid delays.
                          </p>
                        </div>
                      ) : null;
                    })()}
                    <p className="text-amber-800 text-xs font-semibold uppercase tracking-wide mb-2">Important Notes</p>
                    <ul className="text-amber-700 text-xs space-y-1.5 list-none">
                      <li className="flex items-start gap-2"><span className="text-amber-500 font-bold mt-0.5">•</span><span>Lab approvals and provisioning can take <strong>up to 2 weeks</strong>. Kindly ensure the request is placed a minimum of 2 weeks before the workshop start date.</span></li>
                      <li className="flex items-start gap-2"><span className="text-amber-500 font-bold mt-0.5">•</span><span>Once provisioned, you will receive an email containing the <strong>instructor and student lab access process</strong>.</span></li>
                      <li className="flex items-start gap-2"><span className="text-amber-500 font-bold mt-0.5">•</span><span>Once activated, the lab environment will remain available for <strong>30 days</strong> and will be automatically deprovisioned thereafter.</span></li>
                      <li className="flex items-start gap-2"><span className="text-amber-500 font-bold mt-0.5">•</span><span><strong>Lab duration cannot be extended.</strong> Kindly ensure that your participants finish the lab activity within the given time period.</span></li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </form>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            type="submit"
            form="schedule-workshop-form"
            disabled={submitting || !formData.course_id}
            className="w-full bg-levelup-accent text-levelup-darker py-3 rounded-lg font-semibold hover:bg-levelup-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="schedule-submit-button"
          >
            {submitting ? 'Scheduling...' : 'Schedule Workshop'}
          </button>
        </div>
      </div>
    </div>
  );
};
